// Helpers to apply an admin-chosen brand color to the app's CSS variables.
// The theme (globals.css) stores colors as bare HSL triples consumed via
// `hsl(var(--primary))`, so we convert a hex color to that same triple format
// and pick a readable foreground (white/near-black) by luminance.

/** Parses "#rgb" / "#rrggbb" into {r,g,b} (0–255), or null if unparseable. */
function parseHex(hex) {
	if (typeof hex !== "string") return null;
	let value = hex.trim().replace(/^#/, "");
	if (value.length === 3) {
		value = value
			.split("")
			.map((c) => c + c)
			.join("");
	}
	if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
	return {
		r: parseInt(value.slice(0, 2), 16),
		g: parseInt(value.slice(2, 4), 16),
		b: parseInt(value.slice(4, 6), 16),
	};
}

/** Converts a hex color to a bare HSL triple string, e.g. "222 47% 11%". */
export function hexToHslTriple(hex) {
	const rgb = parseHex(hex);
	if (!rgb) return null;
	const r = rgb.r / 255;
	const g = rgb.g / 255;
	const b = rgb.b / 255;
	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const delta = max - min;
	const l = (max + min) / 2;

	let h = 0;
	let s = 0;
	if (delta !== 0) {
		s = delta / (1 - Math.abs(2 * l - 1));
		switch (max) {
			case r:
				h = ((g - b) / delta) % 6;
				break;
			case g:
				h = (b - r) / delta + 2;
				break;
			default:
				h = (r - g) / delta + 4;
		}
		h *= 60;
		if (h < 0) h += 360;
	}
	return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * A readable foreground HSL triple (near-white or near-black) for text/icons
 * sitting on top of the given brand color, chosen by relative luminance.
 */
export function foregroundTripleFor(hex) {
	const rgb = parseHex(hex);
	if (!rgb) return null;
	// Perceived luminance (sRGB, simple coefficients — enough for a threshold).
	const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
	return luminance > 0.6 ? "222 47% 11%" : "0 0% 100%";
}
